import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMe, getNotifications, type NotificationItem } from '../lib/api.js';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
}

/**
 * Sign out: clear the Cloudflare Access session, then return to the app so Access immediately
 * runs the login flow again (lands the user on the login/SSO screen). Note: with Google SSO, if
 * the Google session is still alive the login completes in one click — to fully switch accounts,
 * sign out of Google too. (This is also the recovery path for a stale Access cookie → 403.)
 */
function signOut() {
  window.location.assign(`/cdn-cgi/access/logout?returnTo=${encodeURIComponent(window.location.origin)}`);
}

// ── Notifications ────────────────────────────────────────────────────────────
const READ_KEY = 'krit_notif_read';
const ICON: Record<NotificationItem['type'], string> = { mention: '💬', ic_vote: '🗳️', diligence: '📋', intake: '📥', task: '☑️' };

function loadRead(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || '[]') as string[]); } catch { return new Set(); }
}
function saveRead(s: Set<string>) {
  try { localStorage.setItem(READ_KEY, JSON.stringify([...s].slice(-500))); } catch { /* ignore quota */ }
}
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [read, setRead] = useState<Set<string>>(() => loadRead());
  const ref = useRef<HTMLDivElement>(null);
  const { data } = useQuery({ queryKey: ['notifications'], queryFn: getNotifications, retry: false, refetchInterval: 60_000, refetchOnWindowFocus: true });
  const items = data?.notifications ?? [];
  const count = items.filter((n) => !read.has(n.id)).length;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const markRead = (ids: string[]) => setRead((prev) => { const s = new Set(prev); ids.forEach((i) => s.add(i)); saveRead(s); return s; });
  const openItem = (n: NotificationItem) => { markRead([n.id]); setOpen(false); if (n.dealId) navigate(`/deals/${n.dealId}`); };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${count ? ` (${count} unread)` : ''}`}
        title="Notifications"
        className="relative w-9 h-9 rounded-lg flex items-center justify-center text-[17px] hover:bg-canvas transition-colors"
      >
        <span aria-hidden>🔔</span>
        {count > 0 && (
          <span className="absolute top-0 right-0 min-w-[16px] h-[16px] px-1 rounded-full bg-red text-white text-[9px] font-bold flex items-center justify-center leading-none">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-3 right-3 top-[60px] max-h-[70vh] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[340px] sm:max-h-[460px] overflow-y-auto bg-white border border-line rounded-xl shadow-sh2 z-50">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-line-2 sticky top-0 bg-white">
            <span className="text-[12.5px] font-bold text-navy">Notifications{count > 0 ? ` · ${count} new` : ''}</span>
            {count > 0 && <button onClick={() => markRead(items.map((n) => n.id))} className="text-[11px] font-semibold text-teal-d hover:underline">Mark all read</button>}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-[12px] text-muted">You&rsquo;re all caught up.</div>
          ) : (
            items.map((n) => {
              const unread = !read.has(n.id);
              return (
                <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left flex gap-2.5 px-4 py-2.5 border-b border-line-2 hover:bg-canvas transition-colors ${unread ? 'bg-teal/5' : ''}`}>
                  <span className="text-[15px] leading-none mt-0.5" aria-hidden>{ICON[n.type]}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-navy leading-snug">{n.title}</div>
                    <div className="text-[11px] text-muted mt-0.5 line-clamp-2">{n.detail}</div>
                    <div className="text-[10px] text-muted-2 mt-0.5">{timeAgo(n.ts)}</div>
                  </div>
                  {unread && <span className="w-1.5 h-1.5 rounded-full bg-teal mt-1.5 shrink-0" aria-hidden />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export function Topbar({ onMenu }: { onMenu?: () => void }) {
  const { data, isError } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false });
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const id = data?.identity;
  const name = id?.name ?? 'Signed out';
  const role = id?.role ?? '—';

  const runSearch = () => navigate(term.trim() ? `/deals?q=${encodeURIComponent(term.trim())}` : '/deals');

  return (
    <div className="h-14 bg-white border-b border-line flex items-center gap-3 px-4 sm:px-[22px] sticky top-0 z-30">
      <button
        type="button"
        onClick={onMenu}
        aria-label="Open menu"
        className="md:hidden shrink-0 w-9 h-9 -ml-1 rounded-lg flex items-center justify-center text-navy hover:bg-canvas text-[18px]"
      >☰</button>
      <div className="flex-1 max-w-[440px] relative">
        <span className="absolute left-[11px] top-[9px] text-muted-2">⌕</span>
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
          className="w-full py-[9px] pl-[34px] pr-3 border border-line rounded-lg bg-canvas text-[13px] outline-none focus:border-teal focus:ring-2 focus:ring-teal/15"
          placeholder="Search deals, sectors, bankers… (Enter)"
        />
      </div>
      <div className="flex items-center gap-2 sm:gap-2.5 ml-auto">
        <NotificationBell />
        <div className="w-[30px] h-[30px] rounded-full bg-navy text-white flex items-center justify-center text-[11px] font-bold">
          {id ? initials(name) : '—'}
        </div>
        <div className="hidden sm:block">
          <div className="text-[12px] font-semibold text-navy capitalize">{isError ? 'Session expired' : name}</div>
          <div className="text-[10px] text-muted capitalize">{isError ? 'sign out to re-login' : role}</div>
        </div>
        <button
          type="button"
          onClick={signOut}
          title="Sign out and re-authenticate (fixes an expired session)"
          className={`ml-1.5 px-2.5 py-[7px] rounded-lg text-[12px] font-semibold border transition-colors ${
            isError
              ? 'bg-red text-white border-red hover:opacity-90'
              : 'bg-canvas text-navy border-line hover:bg-line'
          }`}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
